// formatters.ts
import {
  format,
  formatDistanceToNow,
  getTime,
  intervalToDuration,
  isToday,
  parseISO,
} from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import { Session } from 'src/state/session';

type dateValue = Date | string | number | null;

const localeMap: Record<string, Locale> = {
  en: enUS,
  'pt-BR': ptBR,
  es: es,
};

const zeroPad = (num: number | undefined) => String(num).padStart(2, '0');

export const getCurrentLocale = () => {
  const currentLang = Session.getItem('defaultLang') || 'en';
  return localeMap[currentLang] || enUS;
};

export const FormatTime = {
  Date: (
    value: dateValue,
    formatString = 'dd MMM yyyy',
    defaultLang?: boolean,
  ) => {
    if (!value) return '';
    const date = new Date(value);
    return format(date, formatString, {
      locale: defaultLang ? enUS : getCurrentLocale(),
    });
  },

  DateTime: (value: dateValue, formatString = 'dd MMM yyyy p') => {
    if (!value) return '';
    const date = new Date(value);
    return format(date, formatString, { locale: getCurrentLocale() });
  },

  Time: (value: string) => {
    if (!value) return '';
    const date = parseISO(value);
    return date.toLocaleTimeString(getCurrentLocale().code, {
      hour: 'numeric',
      minute: 'numeric',
    });
  },

  Timestamp: (value: dateValue) => {
    if (!value) return '';
    const date = new Date(value);
    return getTime(date);
  },

  DateTimeShortest: (value: dateValue, formatString = 'dd MMM yyyy p') => {
    if (!value) return '';
    const date = new Date(value);
    return isToday(date)
      ? format(date, 'p', { locale: getCurrentLocale() })
      : format(date, formatString, { locale: getCurrentLocale() });
  },

  ToNow: (value: dateValue) => {
    if (!value) return '';
    const date = new Date(value);
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: getCurrentLocale(),
    });
  },

  SecToTime: (value: number) => {
    if (!value) return '';
    const duration = intervalToDuration({ start: 0, end: value });
    return `${duration.hours ? `${zeroPad(duration.hours)}:` : ''}${zeroPad(duration.minutes)}:${zeroPad(duration.seconds)}`;
  },
} as const;
